import {
  Box,
  Button,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { ApprovalStatePolicy } from '@/types/approvals'
import { approvalsRemaining } from '@/utils/approval-actions'
import RejectReasonForm from './RejectReasonForm'
import useRejectReasonForm from './useRejectReasonForm'

type Decisions = {
  onApprove: (policyId: string) => void
  onReject: (policyId: string, reason: string | null) => void
}

type ApprovalPendingPolicyListProps = Decisions & {
  policies: ApprovalStatePolicy[]
  pendingPolicyIds: Set<string>
}

type PendingPolicyRowProps = Decisions & {
  policy: ApprovalStatePolicy
  isActionPending: boolean
  divider: boolean
}

function othersPendingLabel(policy: ApprovalStatePolicy) {
  const others = Math.max(approvalsRemaining(policy) - 1, 0)
  if (others === 0) return 'No others pending'
  return `${others} ${others === 1 ? 'other' : 'others'} pending`
}

function PendingPolicyRow({
  policy,
  isActionPending,
  divider,
  onApprove,
  onReject,
}: PendingPolicyRowProps) {
  const { isOpen, reason, setReason, open, close, confirm, handleKeyDown } =
    useRejectReasonForm({
      onReject: (reason) => onReject(policy.approval_policy_id, reason),
    })

  return (
    <ListItem
      divider={divider}
      onKeyDown={handleKeyDown}
      sx={{ display: 'block', py: 1.25 }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {policy.description}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {othersPendingLabel(policy)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flex: 'none' }}>
          {isOpen ? (
            <Button variant="text" disabled={isActionPending} onClick={close}>
              Cancel
            </Button>
          ) : (
            <>
              <Button
                color="error"
                variant="contained"
                disabled={isActionPending}
                onClick={open}
              >
                Reject
              </Button>
              <Button
                color="success"
                variant="contained"
                disabled={isActionPending}
                onClick={() => onApprove(policy.approval_policy_id)}
              >
                Approve
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      {isOpen && (
        <RejectReasonForm
          reason={reason}
          onReasonChange={setReason}
          onConfirm={confirm}
          disabled={isActionPending}
          sx={{ pt: 1 }}
        />
      )}
    </ListItem>
  )
}

export default function ApprovalPendingPolicyList({
  policies,
  pendingPolicyIds,
  onApprove,
  onReject,
}: ApprovalPendingPolicyListProps) {
  return (
    <Paper
      variant="outlined"
      sx={{ mt: 1.5, borderRadius: 2, background: 'inherit' }}
    >
      <List disablePadding>
        {policies.map((policy, index) => (
          <PendingPolicyRow
            key={policy.approval_policy_id}
            policy={policy}
            isActionPending={pendingPolicyIds.has(policy.approval_policy_id)}
            divider={index < policies.length - 1}
            onApprove={onApprove}
            onReject={onReject}
          />
        ))}
      </List>
    </Paper>
  )
}
